
import React from 'react';
import { Button } from "@/components/ui/button";
import { 
  CheckCircle2,
  Clock,
  CalendarClock,
  X
} from "lucide-react";

interface BookingActionButtonsProps {
  canCheckIn: boolean;
  canCheckOut: boolean;
  canReschedule: boolean;
  isCompleted: boolean;
  isCancelled: boolean;
  isCheckedIn: boolean;
  sessionHasEnded: boolean;
  hasEnabledDevices: boolean;
  checkInWindowMinutes: number;
  createAccessCodePending: boolean;
  checkOutPending: boolean;
  reschedulePending: boolean;
  onCheckIn: () => void;
  onCheckOut: () => void;
  onReschedule: () => void;
  brandColor?: string;
}

export const BookingActionButtons = ({
  canCheckIn,
  canCheckOut,
  canReschedule,
  isCompleted,
  isCancelled,
  isCheckedIn,
  sessionHasEnded,
  hasEnabledDevices,
  checkInWindowMinutes,
  createAccessCodePending,
  checkOutPending,
  reschedulePending,
  onCheckIn,
  onCheckOut,
  onReschedule,
  brandColor
}: BookingActionButtonsProps) => {
  return (
    <div className="flex flex-col sm:flex-row gap-4">
      {/* UPDATED: Only show check-in button if there are enabled devices */}
      {canCheckIn && (
        <Button 
          onClick={onCheckIn}
          disabled={createAccessCodePending}
          className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg h-14 sm:h-11 text-lg sm:text-sm font-semibold px-6 py-3"
        >
          <CheckCircle2 className="h-6 w-6 mr-2" />
          {createAccessCodePending ? "Generating Code..." : "Check In Now"}
        </Button>
      )}
      
      {canCheckOut && (
        <Button 
          onClick={onCheckOut}
          disabled={checkOutPending}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg h-14 sm:h-11 text-lg sm:text-sm font-semibold px-6 py-3"
        >
          <CheckCircle2 className="h-6 w-6 mr-2" />
          {checkOutPending ? "Checking Out..." : "Check Out"}
        </Button>
      )}

      {/* Reschedule Button - Show if booking is not cancelled, not completed, not checked in, and within 2 hour deadline */}
      {canReschedule && (
        <Button 
          onClick={onReschedule}
          disabled={reschedulePending}
          variant="outline"
          className="flex-1 h-12 sm:h-11 text-base sm:text-sm"
          size="lg"
          style={brandColor ? { 
            borderColor: `${brandColor}40`,
            color: brandColor
          } : undefined}
        >
          <CalendarClock className="h-5 w-5 mr-2" />
          {reschedulePending ? "Processing..." : "Reschedule"}
        </Button>
      )}
      
      {isCompleted && (
        <div className="flex-1 bg-emerald-100 rounded-lg p-4 text-center">
          <CheckCircle2 className="h-5 w-5 mx-auto mb-2 text-emerald-600" />
          <p className="text-emerald-700 text-sm font-medium">
            Session Completed
          </p>
        </div>
      )}
      
      {isCancelled && (
        <div className="flex-1 bg-red-100 rounded-lg p-4 text-center">
          <X className="h-5 w-5 mx-auto mb-2 text-red-600" />
          <p className="text-red-700 text-sm font-medium">
            Booking Cancelled
          </p>
        </div>
      )}
      
      {/* UPDATED: Show timing message only when devices are available but check-in isn't ready yet */}
      {!canCheckIn && !canCheckOut && !sessionHasEnded && !isCheckedIn && !isCompleted && !isCancelled && hasEnabledDevices && (
        <div className="flex-1 bg-gray-100 rounded-lg p-4 text-center">
          <Clock className="h-5 w-5 mx-auto mb-2 text-gray-400" />
          <p className="text-gray-600 text-sm">
            Check-in available {checkInWindowMinutes} minutes before session
          </p>
        </div>
      )}
    </div>
  );
};
